import React, { useCallback, useState } from 'react';
import { Building2, Download, Mail, MessageSquare } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { DateRangePicker } from '../../../components/ui/date-range-picker';
import { downloadCsv } from '../../../lib/csv-utils';
import { daysToDateRange, formatPeriodLabel } from '../../../lib/date-range-utils';
import { useToast } from '../../../hooks/useToast';
import { fetchEngageTenants } from './engage-tenants-api';
import { buildEngageTenantCsvLines, getEngageTenantsCsvFilename } from './engage-tenants-csv-utils';
import { EngageTenantsTable } from './EngageTenantsTable';
import { useEngageTenants } from './useEngageTenants';
import styles from './EngageTenantsTab.module.css';

const CSV_PAGE_SIZE = 100;

type SummaryMetricProps = {
  isFailed: boolean;
  isLoading: boolean;
  children: React.ReactNode;
};

function SummaryMetric({ isFailed, isLoading, children }: SummaryMetricProps) {
  if (isFailed) {
    return <span className={styles.metricDash}>—</span>;
  }
  if (isLoading) {
    return <span className={styles.metricSkeleton} aria-hidden />;
  }
  return <>{children}</>;
}

export const EngageTenantsTab: React.FC = () => {
  const [selectedDays, setSelectedDays] = useState<number>(0);
  const [isExporting, setIsExporting] = useState(false);
  const { addToast } = useToast();

  const {
    data,
    summary,
    isLoading,
    isFetching,
    summaryIsLoading,
    summaryError,
    error,
    hasNextPage,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  } = useEngageTenants(selectedDays);

  const periodLabel = formatPeriodLabel(selectedDays);
  const showSummaryPlaceholders = summaryIsLoading && summary === undefined;
  const summaryFailed = summaryError !== null && summaryError !== undefined;

  const handleDownloadCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const dateRange = daysToDateRange(selectedDays);
      let cursor: string | undefined;
      let morePages = true;
      const csvLines: string[] = [];

      while (morePages) {
        const page = await fetchEngageTenants({ cursor, limit: CSV_PAGE_SIZE, ...dateRange });
        const pageCsvLines = buildEngageTenantCsvLines(page.data);
        csvLines.push(...(csvLines.length === 0 ? pageCsvLines : pageCsvLines.slice(1)));
        morePages = page.hasNextPage;
        cursor = page.cursor;
      }

      downloadCsv(getEngageTenantsCsvFilename(selectedDays), csvLines);
    } catch (error) {
      console.error('Failed to export Engage tenants CSV', error);
      addToast({
        variant: 'destructive',
        title: 'Failed to export CSV',
        description:
          error instanceof Error
            ? error.message
            : 'Something went wrong while exporting Engage tenant usage data.',
      });
    } finally {
      setIsExporting(false);
    }
  }, [addToast, selectedDays]);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <DateRangePicker
          selectedDays={selectedDays}
          onSelectDays={setSelectedDays}
          isLoading={isFetching}
        />
        <Button
          variant="ghost"
          onClick={() => {
            void handleDownloadCsv();
          }}
          disabled={isLoading || isFetching || isExporting}>
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? 'Exporting...' : 'Download CSV'}
        </Button>
      </div>

      <div
        className={styles.summaryGrid}
        data-fetching={isFetching ? 'true' : 'false'}
        aria-busy={showSummaryPlaceholders}>
        <Card className={styles.summaryCard} data-fetching={isFetching ? 'true' : 'false'}>
          <div className={styles.summaryCardHeader}>
            <div>
              <p className={styles.summaryTitle}>Active Engage Tenants</p>
              <p className={styles.summaryValue}>
                <SummaryMetric isFailed={summaryFailed} isLoading={showSummaryPlaceholders}>
                  {(summary?.tenantCount ?? 0).toLocaleString()}
                </SummaryMetric>
              </p>
            </div>
            <div className={`${styles.summaryIcon} ${styles.summaryIconTenants}`}>
              <Building2 />
            </div>
          </div>
        </Card>
        <Card className={styles.summaryCard} data-fetching={isFetching ? 'true' : 'false'}>
          <div className={styles.summaryCardHeader}>
            <div>
              <p className={styles.summaryTitle}>Emails Sent ({periodLabel})</p>
              <p className={styles.summaryValue}>
                <SummaryMetric isFailed={summaryFailed} isLoading={showSummaryPlaceholders}>
                  {(summary?.emailsSent ?? 0).toLocaleString()}
                </SummaryMetric>
              </p>
            </div>
            <div className={`${styles.summaryIcon} ${styles.summaryIconEmail}`}>
              <Mail />
            </div>
          </div>
        </Card>
        <Card className={styles.summaryCard} data-fetching={isFetching ? 'true' : 'false'}>
          <div className={styles.summaryCardHeader}>
            <div>
              <p className={styles.summaryTitle}>SMS Sent ({periodLabel})</p>
              <p className={styles.summaryValue}>
                <SummaryMetric isFailed={summaryFailed} isLoading={showSummaryPlaceholders}>
                  {(summary?.smsSent ?? 0).toLocaleString()}
                </SummaryMetric>
              </p>
            </div>
            <div className={`${styles.summaryIcon} ${styles.summaryIconSms}`}>
              <MessageSquare />
            </div>
          </div>
        </Card>
      </div>

      <EngageTenantsTable
        data={data}
        periodLabel={periodLabel}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        hasNextPage={hasNextPage}
        currentPage={currentPage}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
      />
    </div>
  );
};
